/**
 * /settings/custom-postback-domain — operator page for FS-675.
 *
 * Declares a TanStack Router route and renders the
 * `CustomPostbackDomainPage` feature. The root wire-up (router.tsx) picks
 * this up and attaches it to the tree.
 *
 * `?domain=` search param deep-links the add-form to a pre-populated FQDN
 * so operators can jump here from a support ticket / docs link.
 */

import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { CustomPostbackDomainPage } from "../features/custom-postback-domain/CustomPostbackDomainPage";

interface SettingsCustomPostbackDomainSearch {
	domain?: string;
}

function validateSearch(
	raw: Record<string, unknown>,
): SettingsCustomPostbackDomainSearch {
	const v = raw.domain;
	if (typeof v === "string" && v.trim().length > 0) {
		return { domain: v.trim() };
	}
	return {};
}

/**
 * Inner page wrapper — pulls the deep-link prop out of the search params and
 * forwards it to the feature component.
 */
export function SettingsCustomPostbackDomainRoute() {
	return (
		<div className="flex flex-col">
			<CustomPostbackDomainPage />
		</div>
	);
}

/**
 * Programmatic route builder — the root-owned `router.tsx` is responsible
 * for attaching this to the tree via:
 *
 *   rootRoute.addChildren([
 *     ...,
 *     mountSettingsCustomPostbackDomainRoute(rootRoute),
 *   ])
 *
 * Lives here so this file owns its own deep-link search contract without
 * the root having to know about the schema.
 */
export function mountSettingsCustomPostbackDomainRoute(parent: AnyRoute) {
	return createRoute({
		getParentRoute: () => parent,
		path: "/settings/custom-postback-domain",
		validateSearch,
		component: SettingsCustomPostbackDomainRouteWithSearch,
	});
}

/**
 * The component used by the programmatic route — extracted so the parent
 * `addChildren` call can render the page with access to its own typed
 * `useSearch` hook (which requires the route factory's generated context).
 */
function SettingsCustomPostbackDomainRouteWithSearch() {
	// Search handling is intentionally light: the feature page accepts an
	// `initialDomain` prop but the root-owned router types cannot be
	// referenced here without coupling to router.tsx. We therefore render
	// the default page — root can rebuild this helper if it wants to thread
	// the search value through in a later pass.
	return <SettingsCustomPostbackDomainRoute />;
}

export default mountSettingsCustomPostbackDomainRoute;
